import { useState } from 'react';
import type { Schedule } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { useShopPaths } from '../app/nav.tsx';
import { fieldErrors, message } from '../lib/errors.ts';
import { Banner, Button, Card, Chip, Empty, Field, Loading, Page, Sheet, useToast } from '../ui/index.tsx';
import { useConflictRunner } from './conflicts.tsx';
import { useSchedule } from './useSchedule.ts';

type BarberRow = Schedule['barbers'][number];

export function Barbers() {
  const { data, error, reload } = useSchedule();
  const { go } = useShopPaths();
  const [editing, setEditing] = useState<BarberRow | 'new' | null>(null);
  return (
    <Page title="ช่าง" back={() => go('/staff/manage')} actions={<Button className="btn-sm" onClick={() => setEditing('new')}>เพิ่มช่าง</Button>}>
      {error && <Banner kind="error">{error}</Banner>}
      {!data ? <Loading /> : data.barbers.length === 0 ? <Empty title="ยังไม่มีช่าง" action={{ label: 'เพิ่มช่าง', onClick: () => setEditing('new') }} /> : (
        <div className="list">
          {data.barbers.map((b) => (
            <Card key={b.id} as="button" onClick={() => setEditing(b)}>
              <div className="row-between">
                <div className="card-title">{b.name}</div>
                {!b.active && <Chip tone="muted">ปิดรับจอง</Chip>}
              </div>
              <div className="muted small">{b.serviceIds.length} บริการ</div>
            </Card>
          ))}
        </div>
      )}
      {editing && data && <BarberSheet barber={editing === 'new' ? null : editing} schedule={data} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void reload(); }} />}
    </Page>
  );
}

function BarberSheet({ barber, schedule, onClose, onSaved }: { barber: BarberRow | null; schedule: Schedule; onClose: () => void; onSaved: () => void }) {
  const { api } = useShop();
  const toast = useToast();
  const conflicts = useConflictRunner();
  const [name, setName] = useState(barber?.name ?? '');
  const [photoUrl, setPhotoUrl] = useState(barber?.photoUrl ?? '');
  const [sortOrder, setSortOrder] = useState(String(barber?.sortOrder ?? schedule.barbers.length));
  const [serviceIds, setServiceIds] = useState<string[]>(barber?.serviceIds ?? []);
  const [active, setActive] = useState(barber?.active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [invite, setInvite] = useState<string | null>(null);

  const toggle = (id: string) => setServiceIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  async function save() {
    setErrors({});
    await conflicts.run(async (resolutions) => {
      try {
        await api.staff.upsertBarber({
          id: barber?.id, name, photoUrl: photoUrl.trim() || null, sortOrder: Number(sortOrder) || 0, serviceIds, active, resolutions,
        });
      } catch (e) {
        const f = fieldErrors(e);
        if (Object.keys(f).length) { setErrors(f); return; }
        throw e;
      }
    }, { allowKeep: active, onDone: onSaved });
  }

  async function makeInvite() {
    if (!barber) return;
    try {
      const r = await api.staff.invite(barber.id);
      setInvite(r.url);
    } catch (e) {
      toast(message(e), 'error');
    }
  }

  return (
    <Sheet open title={barber ? `แก้ไขช่าง${barber.name}` : 'เพิ่มช่าง'} onClose={onClose}>
      <Field label="ชื่อช่าง" error={errors.name}>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
      </Field>
      <Field label="ลิงก์รูป (ถ้ามี)" hint="วางลิงก์รูปภาพ https://…">
        <input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} inputMode="url" />
      </Field>
      <Field label="ลำดับการแสดง">
        <input type="number" min={0} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </Field>
      <p className="section-title">บริการที่ทำได้</p>
      {schedule.services.map((s) => (
        <label key={s.id} className="check">
          <input type="checkbox" checked={serviceIds.includes(s.id)} onChange={() => toggle(s.id)} />
          <span>{s.name}</span>
        </label>
      ))}
      <label className="check" style={{ marginTop: 8 }}>
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <span>เปิดรับจอง</span>
      </label>
      <div className="stack" style={{ marginTop: 16 }}>
        <Button block onClick={() => void save()}>บันทึก</Button>
        {barber && (
          <>
            <p className="section-title">บัญชี LINE ของช่าง</p>
            {invite ? (
              <Card>
                <p className="small" style={{ wordBreak: 'break-all' }}>{invite}</p>
                <div className="row" style={{ marginTop: 8 }}>
                  <Button variant="secondary" className="btn-sm" onClick={() => { void navigator.clipboard?.writeText(invite); toast('คัดลอกลิงก์แล้ว', 'success'); }}>คัดลอกลิงก์</Button>
                  <a className="btn btn-primary btn-sm" href={`https://line.me/R/msg/text/?${encodeURIComponent(`ลิงก์สำหรับเชื่อมบัญชีช่างกับร้าน (ใช้ได้ 48 ชม.)\n${invite}`)}`}>ส่งทาง LINE</a>
                </div>
              </Card>
            ) : <Button variant="secondary" block onClick={() => void makeInvite()}>สร้างลิงก์เชิญช่าง</Button>}
          </>
        )}
      </div>
      {conflicts.dialog}
    </Sheet>
  );
}
